import {AppDispatch, RootState} from "../../../store";
import {GENERAL, IChatMessage, channelOf} from "../../chat/model/types";
import {maskVin, userName} from "../../board/model/format";
import {ICarEvent} from "../../board/model/types";
import {setMessage} from "../../messages/model/slice";
import {MESSAGE_SEVERITY} from "../../../utils/const";
import {isPageActive, playAlertBeep, playArrivalBeep, showSystemNotification} from "./browser";
import {
    messagePreview,
    notificationTag,
    shouldBeepAlert,
    shouldNotifyStageArrival,
    shouldShowSystemNotification,
    stageArrivalSignals,
} from "./rules";
import {readNotifyEnabled} from "./prefs";
import {NTX} from "./texts";

const context = (state: RootState) => {
    const me = state.users.current?.id ?? null;
    return {me, pageActive: isPageActive(), enabled: readNotifyEnabled(me)};
};

export interface IAlertData {
    car_id?: string;
    vin?: string;
    reason?: string;
    user_id?: string;
}

/** Алерт: звук всем, кроме поднявшего; системное уведомление — если вкладка не на экране. */
export const alertRaised =
    (data: IAlertData, onOpen: (carId: string) => void) => (_: AppDispatch, getState: () => RootState) => {
        const state = getState();
        const ctx = context(state);
        const actorId = data.user_id ?? null;
        if (shouldBeepAlert(actorId, ctx.me, ctx.enabled)) playAlertBeep();
        if (!data.car_id || !shouldShowSystemNotification({type: "alert", actorId}, ctx)) return;
        const carId = data.car_id;
        const vin = data.vin ?? state.board.cars[carId]?.vin ?? "";
        showSystemNotification({
            title: NTX.alertTitle(maskVin(vin)),
            body: data.reason ?? "",
            tag: notificationTag({type: "alert", carId}),
            onClick: () => onOpen(carId),
        });
    };

/**
 * Машина пришла на наш этап (S4.2) — по строке журнала `moved` (`board:event`):
 * в `board:car` нет того, кто перенёс, а своё действие не озвучиваем.
 * Всплывающее всегда; вкладка не на экране — ещё системное уведомление и тихий
 * сигнал, если уведомления не выключены переключателем (S4.3).
 */
export const stageArrivalNotify =
    (e: ICarEvent, onOpen: (carId: string) => void) => (dispatch: AppDispatch, getState: () => RootState) => {
        if (e.type !== "moved" || !e.car_id) return;
        const state = getState();
        const arrival = {
            actorId: e.user?.id ?? null,
            fromStage: (e.payload?.before?.stage as string | undefined) ?? null,
            toStage: (e.payload?.after?.stage as string | undefined) ?? null,
        };
        const me = state.users.current?.id ?? null;
        if (!shouldNotifyStageArrival(arrival, state.board.stages, me)) return;
        const carId = e.car_id;
        const stage = state.board.stages.find((s) => s.key === arrival.toStage)?.title ?? arrival.toStage ?? "";
        const text = NTX.arrived(maskVin(e.vin), stage);
        const signals = stageArrivalSignals(isPageActive(), readNotifyEnabled(me));
        if (signals.toast) dispatch(setMessage({severity: MESSAGE_SEVERITY.info, text}));
        if (signals.sound) playArrivalBeep();
        if (!signals.system) return;
        showSystemNotification({
            title: text,
            body: "",
            tag: notificationTag({type: "stage", carId}),
            onClick: () => onOpen(carId),
        });
    };

/**
 * Веха поставлена вручную (S9) — по строке журнала `milestone_set`: всем, кроме
 * поставившего. Веха из окна закрытия этапа (`via: 'move'`) не озвучивается —
 * о переносе уже оповестил stageArrivalNotify. Сигналы — как у переноса.
 */
export const milestoneNotify =
    (e: ICarEvent, onOpen: (carId: string) => void) => (dispatch: AppDispatch, getState: () => RootState) => {
        if (e.type !== "milestone_set" || !e.car_id || e.payload?.via === "move") return;
        const state = getState();
        const me = state.users.current?.id ?? null;
        if (me && e.user?.id === me) return;
        const carId = e.car_id;
        const kind = e.payload?.kind as string | undefined;
        const title = state.board.milestones.find((m) => m.key === kind)?.title ?? kind ?? "";
        const after = e.payload?.after as {status?: string; remark?: string | null} | undefined;
        const issue = after?.status === "issue";
        const text = (issue ? NTX.milestoneIssue : NTX.milestoneDone)(maskVin(e.vin), title);
        const signals = stageArrivalSignals(isPageActive(), readNotifyEnabled(me));
        if (signals.toast) dispatch(setMessage({severity: MESSAGE_SEVERITY.info, text}));
        if (signals.sound) playArrivalBeep();
        if (!signals.system) return;
        showSystemNotification({
            title: text,
            body: issue ? (after?.remark ?? "") : "",
            tag: notificationTag({type: "milestone", carId}),
            onClick: () => onOpen(carId),
        });
    };

/**
 * Сообщение чата: системное уведомление по правилу shouldShowSystemNotification.
 * `onOpen` — клик по уведомлению (с переходом на доску, `useOpenChannel`).
 */
export const messageNotify = (m: IChatMessage, onOpen?: (channel: string) => void) => (_: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const channel = channelOf(m);
    const event = {type: "message" as const, authorId: m.author?.id ?? null, kind: m.kind, channel};
    if (!shouldShowSystemNotification(event, context(state))) return;
    const author = userName(m.author);
    const vin = state.board.cars[channel]?.vin;
    showSystemNotification({
        title: channel === GENERAL ? NTX.generalTitle(author) : vin ? NTX.messageTitle(author, maskVin(vin)) : author,
        body: messagePreview(m.text, m.attachments.length > 0),
        tag: notificationTag({type: "message", channel}),
        onClick: () => onOpen?.(channel),
    });
};
